import React from 'react';
import NProgress from 'nprogress';
import { useMutation } from '@apollo/client';
import { Address } from '../../models';
import {
  AddressForm,
  reducer as addressReducer,
} from '../shared/Forms/AddressForm';
import {
  CREATE_USER_ADDRESS,
  UPDATE_USER_ADDRESS,
} from '../../graphql/mutation';
import { validateAddressForm } from '../../lib/formValidations';
import { useNotify, useUser } from '../../hooks';
import FormButtons from '../shared/Forms/FormButtons';

interface ProfileAddressProps {
  address: Address;
}

function ProfileAddress({ address }: ProfileAddressProps) {
  const notify = useNotify();

  const [user] = useUser();

  const [disabled, setDisabled] = React.useState(false);

  const [addressState, addressDispatch] = React.useReducer(
    addressReducer,
    address ?? {}
  );

  const [updateUserAddressMutation] = useMutation<{
    updateUserAddress: { address: Address };
  }>(UPDATE_USER_ADDRESS);

  const [createUserAddressMutation] = useMutation<{
    createUserAddress: { address: Address };
  }>(CREATE_USER_ADDRESS);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    try {
      e.preventDefault();
      NProgress.start();
      // disabled validation
      if (disabled) return;
      setDisabled(true);
      // validate form
      const error = validateAddressForm(addressState);
      if (error) {
        notify(error, 'error');
        return;
      }

      // create or update address
      if (address) {
        await updateAddress({
          ...addressState,
        });
      } else {
        await createAddress({
          ...addressState,
        });
      }
    } catch (error) {
      notify(error.message, 'error');
    } finally {
      NProgress.done();
      setDisabled(false);
    }
  };

  const updateAddress = async (addressData: Partial<Address>) => {
    try {
      const { data } = await updateUserAddressMutation({
        variables: {
          data: {
            address: {
              ...addressData,
              userId: user._id,
              __typename: undefined,
            },
          },
        },
      });
      if (data?.updateUserAddress) {
        notify('Dirección actualizada', 'success');
      }
    } catch (error) {
      notify(error.message, 'error');
    }
  };

  const createAddress = async (addressData: Partial<Address>) => {
    try {
      const { data } = await createUserAddressMutation({
        variables: {
          data: { userId: user._id, address: { ...addressData } },
        },
      });
      if (data?.createUserAddress) {
        notify('Dirección creada con exito', 'success');
      }
    } catch (error) {
      notify(error.message, 'error');
    }
  };

  return (
    <form
      method="POST"
      onSubmit={onSubmit}
      className="flex flex-col w-full space-y-10"
    >
      <AddressForm
        dispatch={addressDispatch}
        state={addressState}
        header={{ title: 'Dirección de facturación' }}
      />
      <FormButtons type="update" active disabled={false} onSubmit={onSubmit} />
    </form>
  );
}

export default ProfileAddress;
